import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Row, Select, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ExtrudeMeshGen, ExtrudeSide, PathTransform} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {TransformUI_SharedProps} from "../PathEditorTabs/Transforms.js";

/** The extrude-transform editor: enabled toggle, offset distance, per-side offset copies,
 *  and mesh-gen mode. Mirrors the former inline transform-editor body in Paths_CompUI.tsx
 *  (extracted 2026-08-01 alongside InterpolateUI.tsx when curve-types became transforms). */
export const ExtrudeUI = observer_mgl((props: {transform: PathTransform} & TransformUI_SharedProps)=>{
	const {transform, Change} = props;
	const ex = transform.extrude!;
	// Side options depend on the source path's closed/open topology (resolved by the caller).
	const sourceClosed = props.sourceClosed;
	const sideOptions = sourceClosed
		? [{name: "In", value: "in"}, {name: "Out", value: "out"}]
		: [{name: "Left", value: "left"}, {name: "Right", value: "right"}];

	return (
		<>
			<Row mt={3}>
				<Text>Distance (m):</Text>
				<Spinner ml={5} min={0} step={0.5} value={ex.distance} onChange={val=>Change(t=>t.extrude!.distance = val)}/>
			</Row>
			<Row mt={3}>
				<TextPlus info={`
					One entry per offset copy. Closed source: "in"/"out". Open source: "left"/"right".
					Add/remove entries to control how many offset copies are produced.
				`.AsMultiline(0)}>Sides:</TextPlus>
			</Row>
			{(ex.sides ?? []).map((side: ExtrudeSide, sIndex: number)=>{
				return (
					<Row key={sIndex} mt={2} ml={20}>
						<Select options={sideOptions} value={side} onChange={val=>Change(t=>{ t.extrude!.sides = t.extrude!.sides.map((s: ExtrudeSide, i: number)=>i === sIndex ? val : s); })}/>
						<Button mdIcon="delete" ml={5} title="Remove side" onClick={()=>Change(t=>{ t.extrude!.sides = t.extrude!.sides.filter((_: ExtrudeSide, i: number)=>i !== sIndex); })}/>
					</Row>
				);
			})}
			<Row mt={2} ml={20}>
				<Button mdIcon="plus" text="Add side" onClick={()=>Change(t=>{ t.extrude!.sides = [...(t.extrude!.sides ?? []), sourceClosed ? "out" : "left"]; })}/>
			</Row>
			<Row mt={3}>
				<TextPlus info={`
					How the offset copies are turned into meshes.
					* eachCopySeparate: each copy is its own mesh.
					* origToEachCopy: a mesh is built between the orig points and each copy.
					* copyToCopy: requires exactly two copies; one polygon from copy1 + copy2 reversed.
				`.AsMultiline(0)}>Mesh gen:</TextPlus>
				<Select ml={5}
					options={[
						{name: "Each copy separate", value: "eachCopySeparate"},
						{name: "Orig to each copy", value: "origToEachCopy"},
						{name: "Copy to copy", value: "copyToCopy"}
					] as {name: string, value: ExtrudeMeshGen}[]}
					value={ex.meshGen ?? "eachCopySeparate"}
					onChange={val=>Change(t=>t.extrude!.meshGen = val)}/>
			</Row>
			<Row mt={3}>
				<TextPlus info={`
					Post-pass for inward extrudes ("in" / "right"): detects where the offset polygon
					folds back through itself (miter overshoot on tight corners, peninsulas narrower
					than ~2x the distance) and collapses those loops by dropping the enclosed
					vertices and bridging across the pocket mouth. Consecutive duplicate points are
					removed. Strictly reduces vertex count (never adds vertices).
					Guarantees: (2) no more vertices than the original, (3) no self-intersection.
					Does NOT guarantee (1) result is >= distance from original everywhere (impossible
					with miter-normalized offsets — corners under/overshoot) nor (4) no drift farther
					than distance (accepted residual at reflex corners).
					No-op for "out" / "left" sides.
				`.AsMultiline(0)}>Clean inset:</TextPlus>
				<CheckBox ml={5} value={!!ex.collapseSelfIntersections} onChange={val=>Change(t=>t.extrude!.collapseSelfIntersections = val)}/>
			</Row>
		</>
	);
});
